import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Text,
    Loader,
    SimpleGrid,
    Group,
} from '@mantine/core';
import { Card } from '@xplatform/design-system';
import { useDoctypes } from '@hooks/useContext';
import { useTranslation } from '@hooks/useTranslation';
import EmptyState from '@components/EmptyState/EmptyState';
import heroImg from '@assets/hero.png';
import defaultIcon from '@assets/icons/default.jpg';
import type { DocumentTypeInfoViewModel } from '@src/models/doctype';
import './DoctypePage.scss';

const DoctypePage: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { doctypes, isLoading, error, fetchDoctypes } = useDoctypes();

    React.useEffect(() => {
        fetchDoctypes();
    }, [fetchDoctypes]);

    const handleSelectDoctype = (doctypeCode: string) => {
        navigate(`${encodeURIComponent(doctypeCode)}`);
    };

    const getDisplayName = (doctype: DocumentTypeInfoViewModel): string => {
        const nameKey = `projectNames.${doctype.docTypeCode}`;
        const entityKey = `entityNames.${doctype.docTypeCode}`;
        const name = t(nameKey, { ns: 'project', defaultValue: '' });
        if (name && name !== nameKey) return name;
        const entity = t(entityKey, { ns: 'project', defaultValue: '' });
        if (entity && entity !== entityKey) return entity;
        return doctype.docTypeName;
    };

    const getDescription = (doctype: DocumentTypeInfoViewModel): string => {
        if (doctype.description) return doctype.description;
        const descKey = `descriptions.${doctype.docTypeCode}`;
        const desc = t(descKey, { ns: 'project', defaultValue: '' });
        if (desc && desc !== descKey) return desc;
        return doctype.docTypeCode;
    };

    const getIcon = (doctype: DocumentTypeInfoViewModel): string => {
        if (!doctype.logoUrl) return defaultIcon;

        if (doctype.logoUrl.startsWith('http://') || doctype.logoUrl.startsWith('https://')) {
            return doctype.logoUrl;
        }

        const baseUrl = import.meta.env.VITE_EXTRACTION_API_BASE_URL || '';
        const path = doctype.logoUrl.startsWith('/') ? doctype.logoUrl : `/${doctype.logoUrl}`;
        return `${baseUrl}${path}`;
    };

    const groupedCategories = React.useMemo(() => {
        const groups = new Map<string, { description: string | null; items: DocumentTypeInfoViewModel[] }>();
        for (const doctype of doctypes) {
            const category = doctype.categoryName || 'Other';
            const existing = groups.get(category);
            if (existing) {
                existing.items.push(doctype);
            } else {
                groups.set(category, { description: doctype.categoryDescription ?? null, items: [doctype] });
            }
        }
        return Array.from(groups.entries());
    }, [doctypes]);

    const renderCategory = (categoryName: string, categoryDescription: string | null, items: DocumentTypeInfoViewModel[]) => {
        if (items.length === 0) return null;
        const categoryKey = categoryName.toLowerCase().replace(/\s+/g, '-');
        const titleKey = `projectPage.categories.${categoryKey}.title`;
        const blurbKey = `projectPage.categories.${categoryKey}.description`;
        const translatedTitle = t(titleKey);
        const translatedBlurb = t(blurbKey);

        return (
            <section className="category-section" key={categoryName} aria-labelledby={`cat-${categoryKey}`}>
                <Box className="category-header">
                    <Text id={`cat-${categoryKey}`} size="xl" fw={700} component="h2">
                        {translatedTitle !== titleKey ? translatedTitle : categoryName}
                    </Text>
                    <Text size="md" c="dimmed" className="section-blurb">
                        {categoryDescription || (translatedBlurb !== blurbKey ? translatedBlurb : '')}
                    </Text>
                </Box>
                <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="xl">
                    {items.map((doctype) => (
                        <Card
                            key={doctype.docTypeCode}
                            h="100%"
                            className="project-card clickable"
                            shadow="none"
                            onClick={() => handleSelectDoctype(doctype.docTypeCode)}
                            padding="lg"
                        >
                            <Box className="project-media-inline" aria-hidden>
                                <img
                                    src={getIcon(doctype)}
                                    alt={getDisplayName(doctype)}
                                    className="project-icon"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = defaultIcon;
                                    }}
                                />
                            </Box>
                            <Text size="lg" fw={600} mb="xs" component="h3">
                                {getDisplayName(doctype)}
                            </Text>
                            <Text
                                size="md"
                                c="dimmed"
                                mb="md"
                                className="project-description"
                            >
                                {getDescription(doctype)}
                            </Text>
                        </Card>
                    ))}
                </SimpleGrid>
            </section>
        );
    };

    return (
        <Container size="lg" className="project-page">
            <Box className="page-hero" role="region" aria-label={t('projectPage.hero.ariaLabel')}>
                <Group justify="space-between" align="center" wrap="nowrap">
                    <div>
                        <Text size="xl" fw={800} className="hero-title" component="h1">
                            {t('projectPage.hero.title')}
                        </Text>
                        <Text size="md" c="dimmed">
                            {t('projectPage.hero.description')}
                        </Text>
                    </div>
                    <img src={heroImg} alt={t('projectPage.hero.imageAlt')} className="image-illustration" />
                </Group>
            </Box>

            {isLoading ? (
                <Box className="loading-container" aria-busy="true" aria-live="polite">
                    <Loader size={48} />
                    <Text size="md" mt="md">
                        {t('projectPage.loading')}
                    </Text>
                </Box>
            ) : (
                <>
                    {groupedCategories.map(([categoryName, { description: categoryDescription, items }]) =>
                        renderCategory(categoryName, categoryDescription, items)
                    )}
                </>
            )}

            {!isLoading && doctypes.length === 0 && error && (
                <Box className="error-container">
                    <EmptyState message={t('projectPage.errors.failedToLoad')} variant="h5" />
                    <Text size="md" c="red" mt="xs">
                        {error}
                    </Text>
                    <Text size="md" c="dimmed" mt="xs">
                        {t('projectPage.errors.refreshHelp')}
                    </Text>
                </Box>
            )}

            {!isLoading && doctypes.length === 0 && !error && (
                <Box className="no-projects">
                    <EmptyState message={t('projectPage.empty.noProjects')} variant="h5" />
                    <Text size="md" c="dimmed" mt="xs">
                        {t('projectPage.empty.contactHelp')}
                    </Text>
                </Box>
            )}
        </Container>
    );
};

export default DoctypePage;
